import { useEffect, useRef } from 'react';
import { useCreateBlockNote, SuggestionMenuController } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';
import { resolveMergeTagsInBlocks, type MergeTagContext, MERGE_TAG_DEFS } from './mergeTags';

interface Props {
  content: any[];
  onChange?: (content: any[]) => void;
  editable?: boolean;
  mergeContext?: MergeTagContext;
}

export function ProposalBlockEditor({ content, onChange, editable = true, mergeContext }: Props) {
  const editor = useCreateBlockNote({
    domAttributes: { editor: { class: 'notion-editor' } },
  });
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!editor) return;
    const blocks = editable
      ? content
      : resolveMergeTagsInBlocks(content || [], mergeContext || {});
    if (!initializedRef.current || !editable) {
      if (Array.isArray(blocks) && blocks.length > 0) {
        try {
          editor.replaceBlocks(editor.document, blocks);
        } catch {
          // fallthrough to empty
        }
      }
      initializedRef.current = true;
    }
  }, [editor, content, editable, mergeContext]);

  const handleChange = () => {
    if (!onChange) return;
    onChange(editor.document as any);
  };

  return (
    <div className="proposal-block-editor bg-background rounded-lg">
      <BlockNoteView
        editor={editor}
        editable={editable}
        onChange={editable ? handleChange : undefined}
        theme="light"
      >
        {/* Variable autocomplete: type [ to trigger */}
        {editable && (
          <SuggestionMenuController
            triggerCharacter="["
            getItems={async (query: string) => {
              const q = query.toLowerCase();
              const filtered = MERGE_TAG_DEFS.filter(
                t =>
                  t.tag.toLowerCase().includes(q) ||
                  t.label.toLowerCase().includes(q) ||
                  t.category.toLowerCase().includes(q)
              );
              return filtered.map(t => ({
                // `key` is required by DefaultSuggestionItem but we cast to any
                // to avoid importing the full dictionary type
                title: t.tag,
                subtext: t.label,
                group: t.category,
                aliases: [t.label.toLowerCase(), t.category.toLowerCase()],
                onItemClick: () => {
                  // SuggestionMenuController removes trigger + query automatically,
                  // then calls onItemClick. We just insert the tag text with a highlight.
                  // We add a trailing unstyled space to prevent the highlight from bleeding into next text.
                  editor.insertInlineContent([
                    { type: 'text', text: t.tag, styles: { backgroundColor: 'yellow' } },
                    { type: 'text', text: ' ', styles: {} }
                  ]);
                },
              })) as any[];
            }}
          />
        )}
      </BlockNoteView>
    </div>
  );
}
